import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/lib/api-client";
import { API_URL } from "@/config/api";
import { getCurrentUser } from "@/hooks/use-auth";

/** One item in the chat: text from either side, or a card from the assistant. */
export interface AssistantMessage {
  role: "user" | "assistant" | "error";
  type: string; // text | order_draft | order_placed | booking_link | order_link | whatsapp | login | error
  text?: string;
  createdAt?: string;
  [key: string]: any;
}

const GUEST_ID_KEY = "assistant.guestId";
const conversationKey = (identity: string) => `assistant.conversation.${identity}`;

function newGuestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // RFC 4122 v4 for older browsers.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getGuestId(): string {
  let id = localStorage.getItem(GUEST_ID_KEY);
  if (!id) {
    id = newGuestId();
    localStorage.setItem(GUEST_ID_KEY, id);
  }
  return id;
}

/** Who is chatting right now: a logged-in user, or this browser as a guest. */
export function currentAssistantIdentity(): string {
  const user = getCurrentUser();
  return apiClient.getToken() && user?.id ? `user:${user.id}` : "guest";
}

export function useAssistantStatus() {
  return useQuery({
    queryKey: ["/assistant/status"],
    queryFn: async (): Promise<{ enabled: boolean }> => {
      const response = await fetch(`${API_URL}/assistant/status`);
      if (!response.ok) return { enabled: false };
      return response.json();
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

async function readError(response: Response): Promise<{ code?: string; message?: string }> {
  try {
    const body = await response.json();
    const message = Array.isArray(body?.message) ? body.message.join(", ") : body?.message;
    return { code: body?.code, message };
  } catch {
    return {};
  }
}

/**
 * Chat state for the assistant widget. The conversation id is remembered per
 * identity, so logging in or out switches to that identity's own chat.
 */
export function useAssistant(identity: string) {
  const { t, i18n } = useTranslation();
  const [conversationId, setConversationId] = useState<string | null>(() =>
    localStorage.getItem(conversationKey(identity)),
  );
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const loadedFor = useRef<string | null>(null);

  const language = i18n.language?.startsWith("en") ? "en" : "ar";
  const guestQuery = identity === "guest" ? `?guestId=${encodeURIComponent(getGuestId())}` : "";

  // Identity changed (login/logout): switch to that identity's conversation.
  useEffect(() => {
    setConversationId(localStorage.getItem(conversationKey(identity)));
    setMessages([]);
    loadedFor.current = null;
  }, [identity]);

  const remember = useCallback(
    (id: string | null) => {
      setConversationId(id);
      if (id) localStorage.setItem(conversationKey(identity), id);
      else localStorage.removeItem(conversationKey(identity));
    },
    [identity],
  );

  /** Load the saved conversation once per identity (call when the panel opens). */
  const loadHistory = useCallback(async () => {
    if (!conversationId || loadedFor.current === `${identity}:${conversationId}`) return;
    loadedFor.current = `${identity}:${conversationId}`;
    setLoadingHistory(true);
    try {
      const response = await apiClient.get(`/assistant/conversations/${conversationId}${guestQuery}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      } else if (response.status === 404) {
        remember(null); // expired or deleted on the server
      }
    } catch {
      // Offline: start fresh, the server copy is still there.
    } finally {
      setLoadingHistory(false);
    }
  }, [conversationId, identity, guestQuery, remember]);

  const pushError = useCallback((text: string) => {
    setMessages((prev) => [...prev, { role: "error", type: "error", text }]);
  }, []);

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || sending) return;
      setSending(true);
      setMessages((prev) => [
        ...prev.filter((m) => m.role !== "error"),
        { role: "user", type: "text", text: message, createdAt: new Date().toISOString() },
      ]);
      try {
        const response = await apiClient.post("/assistant/chat", {
          message,
          guestId: getGuestId(),
          language,
          ...(conversationId ? { conversationId } : {}),
        });
        if (!response.ok) {
          const error = await readError(response);
          if (response.status === 404 && conversationId) remember(null);
          pushError(
            response.status === 429
              ? t("assistant.errors.limit")
              : response.status === 409
                ? t("assistant.errors.busy")
                : t("assistant.errors.generic"),
          );
          if (error.message) console.warn("assistant:", error.message);
          return;
        }
        const data = await response.json();
        remember(data.conversationId);
        loadedFor.current = `${identity}:${data.conversationId}`;
        const incoming: AssistantMessage[] = data.messages || [];
        // A new order card replaces any earlier one (the server cancels those too).
        const newCard = incoming.some((m) => m.type === "order_draft");
        setMessages((prev) => [
          ...(newCard
            ? prev.map((m) => (m.type === "order_draft" && (m.status ?? "pending") === "pending" ? { ...m, status: "cancelled" } : m))
            : prev),
          ...incoming,
        ]);
      } catch {
        pushError(t("assistant.errors.offline"));
      } finally {
        setSending(false);
      }
    },
    [sending, conversationId, language, identity, remember, pushError, t],
  );

  /** Forget this chat (and delete it on the server) to start over. */
  const reset = useCallback(async () => {
    const id = conversationId;
    remember(null);
    setMessages([]);
    loadedFor.current = null;
    if (id) {
      apiClient.delete(`/assistant/conversations/${id}${guestQuery}`).catch(() => {});
    }
  }, [conversationId, guestQuery, remember]);

  const updateCard = useCallback((draftId: string, patch: Record<string, any>) => {
    setMessages((prev) => prev.map((m) => (m.type === "order_draft" && m.draftId === draftId ? { ...m, ...patch } : m)));
  }, []);

  /**
   * The customer's click on "Confirm order". Same follow-up as the booking
   * page: the payment page when something is left to pay, the success page
   * when the wallet covered everything.
   */
  const confirmDraft = useCallback(
    async (draftId: string) => {
      updateCard(draftId, { busy: true, error: undefined });
      try {
        const response = await apiClient.post(`/assistant/order-drafts/${draftId}/confirm`);
        if (response.ok) {
          const result = await response.json();
          updateCard(draftId, { busy: false, status: "confirmed", orderId: result.orderId });
          if (result.paymentLink) {
            window.location.href = result.paymentLink;
          } else {
            window.location.href = `/payment/success?orderId=${result.orderId}`;
          }
          return;
        }
        const error = await readError(response);
        const status =
          error.code === "already_confirmed"
            ? "confirmed"
            : error.code === "cancelled"
              ? "cancelled"
              : error.code === "price_changed"
              ? "stale"
              : error.code === "expired" || response.status === 410
                ? "expired"
                : error.code === "order_failed"
                  ? "failed"
                  : undefined;
        updateCard(draftId, {
          busy: false,
          ...(status ? { status } : {}),
          error:
            status === "stale"
              ? t("assistant.order.stale")
              : status === "expired"
                ? t("assistant.order.expired")
                : status === "failed"
                  ? t("assistant.order.failed")
                  : status === "confirmed"
                    ? undefined
                    : t("assistant.errors.generic"),
        });
      } catch {
        updateCard(draftId, { busy: false, error: t("assistant.errors.offline") });
      }
    },
    [t, updateCard],
  );

  const cancelDraft = useCallback(
    async (draftId: string) => {
      updateCard(draftId, { busy: true });
      const response = await apiClient.post(`/assistant/order-drafts/${draftId}/cancel`).catch(() => null);
      updateCard(draftId, { busy: false, ...(response?.ok ? { status: "cancelled" } : {}) });
    },
    [updateCard],
  );

  return { messages, sending, loadingHistory, loadHistory, send, reset, confirmDraft, cancelDraft, language };
}
